import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  ChevronRight,
  ArrowRight,
  Sparkles,
  Building2,
  Check,
  Copy,
} from "lucide-react";
import {
  upsertMerchant,
  fetchUserMerchants,
  selectMerchants,
  selectMerchantLoading,
  Merchant,
} from "../../redux/merchantSlice";
import { AppDispatch, RootState } from "../../redux/store";
import countries from "../../GLOBAL/Country";
import logo from "../../assets/app/logo.png";

/* ─────────────────────────────────────────────────────────────
   Ebuos merchant onboarding — CHUNKY 3D BEVEL PASS.
   Same accent system as before (blue → pink → orange → yellow →
   green wordmark gradient, pink pulled out as the small-UI
   accent), but every surface now sits on a solid offset shadow
   instead of a blurred glow — beads, buttons, plates and the
   hero slab all have a hard drop underneath them and physically
   press down on click, the same mechanic as the dashboard shell
   (mw-btn / mw-plate / mw-bead) instead of a flat glass look.
───────────────────────────────────────────────────────────── */

const BG            = "#08080C"; // page background
const SURFACE       = "#131319"; // card background
const SURFACE_SOFT  = "#1B1B24"; // inset fields, chips
const BORDER        = "rgba(255,255,255,.08)";
const BORDER_STRONG = "rgba(255,255,255,.16)";
const TEXT          = "#F2F2F5"; // primary text
const TEXT_DIM      = "#A5A8BD"; // secondary text
const TEXT_FAINT    = "#63667A"; // labels / captions

const EBUOS_BLUE   = "#4C6FEE";
const EBUOS_PINK   = "#E93E6D";
const EBUOS_ORANGE = "#F3821E";
const EBUOS_YELLOW = "#E8C51E";
const EBUOS_GREEN  = "#3FBE55";
const EBUOS_GRADIENT      = `linear-gradient(90deg, ${EBUOS_BLUE} 0%, ${EBUOS_PINK} 28%, ${EBUOS_ORANGE} 52%, ${EBUOS_YELLOW} 74%, ${EBUOS_GREEN} 100%)`;
const EBUOS_GRADIENT_DIAG = `linear-gradient(150deg, ${EBUOS_BLUE} 0%, ${EBUOS_PINK} 42%, ${EBUOS_ORANGE} 78%, ${EBUOS_YELLOW} 100%)`;

const ACCENT      = EBUOS_PINK;
const ACCENT_GLOW = "rgba(233,62,109,.4)";
const ACCENT_SOFT = "rgba(233,62,109,.14)";

const SAGE      = EBUOS_GREEN;
const SAGE_SOFT = "rgba(63,190,85,.14)";

const GOLD      = "#E8C51E";
const GOLD_SOFT = "rgba(232,197,30,.14)";

const RED      = "#FF5C6C";
const RED_SOFT = "rgba(255,92,108,.14)";

/* Deep offset shades — the solid color each bevel "block" shadow sits in.
   Neutral surfaces (plates, glass beads, fields) drop onto near-black;
   the primary gradient bead/button drops onto a deep pink so it still
   reads as part of the wordmark family. */
const DEEP_NEUTRAL = "#000000";
const DEEP_ACCENT  = "#7A1636";

const DOT = "\u00b7";

/* Beveled edge: a soft top highlight + a soft inner shadow along the
   bottom, applied INSIDE every 3D surface before the solid offset drop
   shadow underneath it. */
const BEVEL_HI = "inset 0 1.5px 0 rgba(255,255,255,.16), inset 0 -3px 0 rgba(0,0,0,.35)";

const SPRING = { type: "spring" as const, stiffness: 320, damping: 26 };

const STEPS = [
  { id: 1, label: "Business", sub: "Company identity" },
  { id: 2, label: "Contact", sub: "Your details" },
  { id: 3, label: "Confirm", sub: "Review & register" },
];
const BUSINESS_TYPES = ["individual", "company", "nonprofit", "government"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Safe display helper — every place that reads a possibly-missing merchant
   field (businessType, currency, etc.) goes through this instead of calling
   .charAt/.toUpperCase directly on something TS (correctly) says may be
   undefined at runtime for older/partial records. */
const title = (s?: string | null) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "—");
const upper = (s?: string | null) => (s ? s.toUpperCase() : "—");

/* ═══════════════════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');

.eb, .eb *, .eb *::before, .eb *::after{ box-sizing:border-box; }
.eb{ position:relative; min-height:100vh; overflow-x:hidden; font-family:'Space Grotesk',system-ui,sans-serif; font-weight:500; color:${TEXT}; background:${BG}; -webkit-font-smoothing:antialiased; }
.eb h1,.eb h2,.eb h3,.eb h4{ font-family:'Fredoka',system-ui,sans-serif; font-weight:600; margin:0; letter-spacing:-.03em; line-height:1.05; }
.eb button{ cursor:pointer; border:none; background:none; font-family:inherit; color:inherit; }
.eb input, .eb select{ font-family:inherit; }
.eb :focus-visible{ outline:3px solid ${ACCENT}; outline-offset:3px; border-radius:16px; }

.eb-mono{ font-family:'IBM Plex Mono',ui-monospace,monospace; font-weight:600; font-size:10px; letter-spacing:.2em; text-transform:uppercase; }

@keyframes ebRise{ from{opacity:0; transform:translateY(18px);} to{opacity:1; transform:none;} }
@keyframes ebWiggle{ 0%{transform:rotate(0deg);} 25%{transform:rotate(-7deg);} 50%{transform:rotate(6deg);} 75%{transform:rotate(-3deg);} 100%{transform:rotate(0deg);} }
@keyframes ebFloat{ 0%,100%{transform:translateY(0);} 50%{transform:translateY(-5px);} }
.eb-rise{ animation:ebRise .5s cubic-bezier(.2,.8,.25,1) both; }
.eb-float{ animation:ebFloat 3.4s ease-in-out infinite; }
@media(prefers-reduced-motion:reduce){ .eb *, .eb *::before, .eb *::after{ animation:none !important; transition:none !important; } }

.eb-light{ position:fixed; inset:0; z-index:0; pointer-events:none;
  background:
    radial-gradient(38% 30% at 8% 0%, rgba(76,111,238,.22) 0%, transparent 68%),
    radial-gradient(34% 28% at 95% 18%, rgba(233,62,109,.16) 0%, transparent 65%),
    radial-gradient(36% 30% at 60% 100%, rgba(63,190,85,.12) 0%, transparent 65%); }
.eb-shell{ position:relative; z-index:1; max-width:1100px; margin:0 auto; padding:0 18px; }

/* ── beads: chunky 3D pucks that press down on click ── */
.eb-bead{ width:46px; height:46px; border-radius:50%; flex-shrink:0; padding:0; display:inline-flex; align-items:center; justify-content:center;
  background:${EBUOS_GRADIENT}; color:#fff;
  box-shadow:${BEVEL_HI}, 0 6px 0 ${DEEP_ACCENT};
  transition:transform .16s cubic-bezier(.2,.9,.3,1.6), box-shadow .16s; }
.eb-bead:hover{ transform:translateY(-3px); box-shadow:${BEVEL_HI}, 0 9px 0 ${DEEP_ACCENT}; }
.eb-bead:hover svg{ animation:ebWiggle .5s ease-in-out; }
.eb-bead:active{ transform:translateY(4px); box-shadow:${BEVEL_HI}, 0 2px 0 ${DEEP_ACCENT}; }
.eb-bead.sm{ width:36px; height:36px; }
.eb-bead.glass{ background:${SURFACE_SOFT}; color:${TEXT}; box-shadow:${BEVEL_HI}, 0 5px 0 ${DEEP_NEUTRAL}; }
.eb-bead.glass:hover{ box-shadow:${BEVEL_HI}, 0 8px 0 ${DEEP_NEUTRAL}; }
.eb-bead.glass:active{ box-shadow:${BEVEL_HI}, 0 2px 0 ${DEEP_NEUTRAL}; }

/* ── buttons: same press-block mechanic ── */
.eb-btn{ display:inline-flex; align-items:center; justify-content:center; gap:9px; white-space:nowrap;
  font-family:'Fredoka',sans-serif; font-weight:600; font-size:14.5px; padding:14px 24px; border-radius:999px;
  background:${SURFACE_SOFT}; color:${TEXT};
  box-shadow:${BEVEL_HI}, 0 6px 0 ${DEEP_NEUTRAL};
  transition:transform .16s cubic-bezier(.2,.9,.3,1.6), box-shadow .16s, opacity .16s; }
.eb-btn:hover:not(:disabled){ transform:translateY(-3px); box-shadow:${BEVEL_HI}, 0 9px 0 ${DEEP_NEUTRAL}; }
.eb-btn:active:not(:disabled){ transform:translateY(5px); box-shadow:${BEVEL_HI}, 0 1px 0 ${DEEP_NEUTRAL}; }
.eb-btn:disabled{ opacity:.5; cursor:not-allowed; }
.eb-btn.primary{ background:${EBUOS_GRADIENT}; color:#fff; text-shadow:0 1px 2px rgba(0,0,0,.25);
  box-shadow:${BEVEL_HI}, 0 7px 0 ${DEEP_ACCENT}; }
.eb-btn.primary:hover:not(:disabled){ box-shadow:${BEVEL_HI}, 0 10px 0 ${DEEP_ACCENT}; }
.eb-btn.primary:active:not(:disabled){ box-shadow:${BEVEL_HI}, 0 2px 0 ${DEEP_ACCENT}; }
.eb-btn.primary:hover:not(:disabled) svg{ transform:translateX(3px); }
.eb-btn svg{ transition:transform .18s ease; }
.eb-btn.ghost{ background:rgba(255,255,255,.14); color:#fff; box-shadow:${BEVEL_HI}, 0 5px 0 rgba(0,0,0,.3); }
.eb-btn.ghost:hover{ box-shadow:${BEVEL_HI}, 0 8px 0 rgba(0,0,0,.3); transform:translateY(-3px); }
.eb-btn.ghost:active{ box-shadow:${BEVEL_HI}, 0 1px 0 rgba(0,0,0,.3); transform:translateY(4px); }
.eb-btn.outline{ background:${SURFACE}; color:${TEXT}; box-shadow:${BEVEL_HI}, 0 6px 0 ${DEEP_NEUTRAL}; }
.eb-btn.outline:hover{ box-shadow:${BEVEL_HI}, 0 9px 0 ${DEEP_NEUTRAL}; transform:translateY(-3px); }
.eb-btn.outline:active{ box-shadow:${BEVEL_HI}, 0 1px 0 ${DEEP_NEUTRAL}; transform:translateY(5px); }
.eb-btn.sm{ padding:10px 17px; font-size:12.5px; }

/* ── plates: raised cards, sink slightly on press ── */
.eb-plate{ position:relative; background:${SURFACE}; border-radius:30px; padding:22px; overflow:hidden;
  box-shadow:${BEVEL_HI}, 0 8px 0 ${DEEP_NEUTRAL}; }
.eb-plate.press{ transition:transform .18s cubic-bezier(.2,.9,.3,1.6), box-shadow .18s; }
.eb-plate.press:hover{ transform:translateY(-4px); box-shadow:${BEVEL_HI}, 0 12px 0 ${DEEP_NEUTRAL}; }
.eb-plate.press:active{ transform:translateY(3px); box-shadow:${BEVEL_HI}, 0 3px 0 ${DEEP_NEUTRAL}; }

/* the only surface carrying the whole wordmark spread — sits on a deep
   pink block so it still reads as one chunky 3D unit, not a flat panel */
.eb-slab{ position:relative; border-radius:34px; padding:clamp(22px,4vw,36px); overflow:hidden;
  background:${EBUOS_GRADIENT_DIAG}; color:#fff;
  box-shadow:inset 0 1.5px 0 rgba(255,255,255,.22), inset 0 -3px 0 rgba(0,0,0,.25), 0 10px 0 ${DEEP_ACCENT}; }

.eb-field{ display:flex; align-items:center; gap:10px; padding:13px 16px; border-radius:20px; background:${SURFACE_SOFT};
  box-shadow:inset 0 0 0 1.5px ${BORDER_STRONG}, inset 0 -3px 0 rgba(0,0,0,.25); transition:box-shadow .18s; }
.eb-field:focus-within{ box-shadow:inset 0 0 0 1.5px ${ACCENT}, inset 0 -3px 0 rgba(0,0,0,.25), 0 0 0 4px ${ACCENT_SOFT}; }
.eb-field input, .eb-field select{ flex:1; min-width:0; border:none; outline:none; background:transparent; color:${TEXT}; font-size:14.5px; font-weight:600; }
.eb-field input::placeholder{ color:${TEXT_FAINT}; font-weight:500; }
.eb-field.trigger{ cursor:pointer; width:100%; text-align:left; }
.eb-field.trigger:hover{ box-shadow:inset 0 0 0 1.5px ${ACCENT}, inset 0 -3px 0 rgba(0,0,0,.25); }

.eb-label{ font-family:'IBM Plex Mono',monospace; font-weight:600; font-size:10px; letter-spacing:.18em; color:${TEXT_DIM}; text-transform:uppercase; margin:0 0 8px 4px; display:block; }

.eb-scrim{ position:fixed; inset:0; z-index:940; background:rgba(0,0,0,.72); backdrop-filter:blur(8px); }

.eb-tag{ display:inline-flex; align-items:center; gap:5px; padding:5px 12px; border-radius:999px; font-family:'IBM Plex Mono',monospace; font-weight:600; font-size:9px; letter-spacing:.12em; text-transform:uppercase; }

.eb-steps{ display:flex; gap:10px; }
@media(max-width:760px){ .eb-steps{ display:none; } }
`;

/* ═══════════════════════════════════════════════════════════
   3D SPHERE — flags orbiting a floating glass globe
═══════════════════════════════════════════════════════════ */
type SphereItem = { code: string; name: string; flag: string; sub?: string };

const useFibonacciSphere = (n: number, radius: number) =>
  useMemo(() => {
    const pts: { x: number; y: number; z: number }[] = [];
    const count = Math.max(n, 1);
    const offset = 2 / count;
    const increment = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < count; i++) {
      const y = i * offset - 1 + offset / 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const phi = i * increment;
      pts.push({ x: Math.cos(phi) * r * radius, y: y * radius, z: Math.sin(phi) * r * radius });
    }
    return pts;
  }, [n, radius]);

const Sphere3D: React.FC<{
  items: SphereItem[];
  radius: number;
  height: number;
  selected: string;
  onPick: (code: string) => void;
}> = ({ items, radius, height, selected, onPick }) => {
  const pts = useFibonacciSphere(items.length, radius);
  const [angleY, setAngleY] = useState(0);
  const [angleX, setAngleX] = useState(-10);
  const drag = useRef({ x: 0, y: 0, active: false });

  const raf = useRef<number | undefined>(undefined);

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const tick = () => {
      if (!drag.current.active) setAngleY((a) => a + 0.3);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current !== undefined) cancelAnimationFrame(raf.current);
    };
  }, []);

  const rad = (d: number) => (d * Math.PI) / 180;

  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    drag.current = { x: e.clientX, y: e.clientY, active: true };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    drag.current.x = e.clientX;
    drag.current.y = e.clientY;
    setAngleY((a) => a + dx * 0.45);
    setAngleX((a) => Math.max(-70, Math.min(70, a - dy * 0.35)));
  };
  const onUp = () => {
    drag.current.active = false;
  };

  if (items.length === 0) {
    return (
      <div
        style={{
          height,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          alignItems: "center",
          justifyContent: "center",
          color: TEXT_FAINT,
          fontSize: 13,
        }}
      >
        <span style={{ fontSize: 26 }} className="eb-float">
          🔍
        </span>
        No country matches that name. Try fewer letters.
      </div>
    );
  }

  return (
    <div
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerLeave={onUp}
      style={{
        position: "relative",
        height,
        perspective: 900,
        touchAction: "none",
        cursor: "grab",
        background: `radial-gradient(circle at 50% 46%, ${ACCENT_SOFT} 0%, transparent 62%)`,
        borderRadius: 26,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: radius * 1.7,
          height: radius * 1.7,
          transform: "translate(-50%,-50%)",
          borderRadius: "50%",
          boxShadow: `inset 0 0 0 1.5px ${BORDER_STRONG}, inset 0 0 40px ${ACCENT_SOFT}`,
          pointerEvents: "none",
        }}
      />
      {items.map((it, i) => {
        const p = pts[i] || { x: 0, y: 0, z: 0 };
        const cosY = Math.cos(rad(angleY));
        const sinY = Math.sin(rad(angleY));
        let x = p.x * cosY + p.z * sinY;
        let z = -p.x * sinY + p.z * cosY;
        let y = p.y;
        const cosX = Math.cos(rad(angleX));
        const sinX = Math.sin(rad(angleX));
        const y2 = y * cosX - z * sinX;
        const z2 = y * sinX + z * cosX;
        y = y2;
        z = z2;

        const depth = (z + radius) / (2 * radius);
        const scale = 0.55 + depth * 0.75;
        const opacity = 0.35 + depth * 0.65;
        const isSel = it.code === selected;

        return (
          <button
            key={it.code}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onPick(it.code)}
            title={it.name}
            aria-label={it.name}
            aria-pressed={isSel}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: `translate3d(${x}px, ${y}px, ${z}px) translate(-50%,-50%) scale(${scale})`,
              zIndex: Math.round(z + 1000),
              opacity,
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 21,
              background: isSel ? EBUOS_GRADIENT : SURFACE_SOFT,
              boxShadow: isSel
                ? `${BEVEL_HI}, 0 4px 0 ${DEEP_ACCENT}`
                : `${BEVEL_HI}, 0 3px 0 ${DEEP_NEUTRAL}`,
            }}
          >
            {it.flag}
          </button>
        );
      })}
    </div>
  );
};

/* the launcher: field + floating card (desktop) / bottom sheet (mobile) */
const SphereSelect: React.FC<{
  label: string;
  items: SphereItem[];
  value: SphereItem;
  onChange: (it: SphereItem) => void;
  isMobile: boolean;
}> = ({ label, items, value, onChange, isMobile }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) => it.name.toLowerCase().includes(q) || it.code.toLowerCase().includes(q)
    );
  }, [items, query]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  return (
    <div>
      <label className="eb-label">{label}</label>
      <button
        type="button"
        className="eb-field trigger"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span style={{ fontSize: 20 }}>{value.flag}</span>
        <span style={{ flex: 1, textAlign: "left" }}>{value.name}</span>
        <ChevronRight
          size={15}
          strokeWidth={2.6}
          style={{ transform: "rotate(90deg)", color: TEXT_FAINT, flexShrink: 0 }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="eb-scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
            />
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label={`Pick a ${label.toLowerCase()}`}
              initial={isMobile ? { y: "100%" } : { opacity: 0, y: 16, scale: 0.95 }}
              animate={isMobile ? { y: 0 } : { opacity: 1, y: 0, scale: 1 }}
              exit={isMobile ? { y: "100%" } : { opacity: 0, y: 16, scale: 0.95 }}
              transition={SPRING}
              style={
                isMobile
                  ? {
                      position: "fixed",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 941,
                      background: SURFACE,
                      borderRadius: "30px 30px 0 0",
                      padding: "18px 18px 28px",
                      boxShadow: `inset 0 1.5px 0 rgba(255,255,255,.14), 0 -8px 0 ${DEEP_NEUTRAL}`,
                      maxHeight: "84vh",
                      display: "flex",
                      flexDirection: "column",
                    }
                  : {
                      position: "fixed",
                      zIndex: 941,
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%,-50%)",
                      width: "min(430px, calc(100% - 40px))",
                      background: SURFACE,
                      borderRadius: 32,
                      padding: 24,
                      boxShadow: `${BEVEL_HI}, 0 10px 0 ${DEEP_NEUTRAL}`,
                    }
              }
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <span className="eb-bead sm glass">
                  <Sparkles size={15} strokeWidth={2.6} />
                </span>
                <h3 style={{ fontSize: 19 }}>Pick a {label.toLowerCase()}</h3>
                <button
                  type="button"
                  className="eb-bead sm glass"
                  style={{ marginLeft: "auto" }}
                  onClick={close}
                  aria-label="Close"
                >
                  <X size={15} strokeWidth={3} />
                </button>
              </div>

              <div className="eb-field" style={{ marginBottom: 12 }}>
                <Search size={16} strokeWidth={2.6} style={{ color: TEXT_FAINT, flexShrink: 0 }} />
                <input
                  autoFocus
                  placeholder={`Search ${label.toLowerCase()}…`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>

              <Sphere3D
                items={filtered.slice(0, 140)}
                radius={isMobile ? 96 : 118}
                height={isMobile ? 250 : 290}
                selected={value.code}
                onPick={(code) => {
                  const it = items.find((i) => i.code === code);
                  if (it) {
                    onChange(it);
                    close();
                  }
                }}
              />
              <p className="eb-mono" style={{ textAlign: "center", color: TEXT_FAINT, marginTop: 10 }}>
                Drag to spin {DOT} tap to pick
              </p>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

type ViewMode = "list" | "form";

/* ═══════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════ */
export default function MerchantOnboarding() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const user = useSelector((s: RootState) => s.auth.user);
  const merchants = useSelector(selectMerchants);
  const storeLoading = useSelector(selectMerchantLoading);

  const [view, setView] = useState<ViewMode>("list");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 800 : false
  );

  const redirectTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(
    () => () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    },
    []
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const uid = user?.userId;
    if (uid && !fetched) {
      dispatch(fetchUserMerchants(uid))
        .unwrap()
        .then(() => setFetched(true))
        .catch(() => setFetched(true));
    }
  }, [user?.userId, dispatch, fetched]);

  const countryItems: SphereItem[] = useMemo(
    () => countries.map((c: any) => ({ code: c.code, name: c.name, flag: c.emoji })),
    []
  );

  const defaultCountry: SphereItem = useMemo(
    () =>
      countryItems.find((c) => c.code === "DE") ??
      countryItems[0] ?? { code: "DE", name: "Germany", flag: "🇩🇪" },
    [countryItems]
  );

  const [form, setForm] = useState({
    businessName: "",
    businessType: "company",
    country: defaultCountry,
    email: "",
    phone: "",
    website: "",
    metadata: {} as Record<string, any>,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const resetForm = () => {
    setForm({
      businessName: "",
      businessType: "company",
      country: defaultCountry,
      email: "",
      phone: "",
      website: "",
      metadata: {},
    });
    setStep(1);
    setError(null);
    setSuccess(false);
  };

  const openMerchant = useCallback(
  (m: Merchant) => {
    if (!m?.merchantId) {
      setError("That business has no ID yet. Refresh and try again.");
      return;
    }
    navigate(`/subscription/${encodeURIComponent(m.merchantId)}`);
  },
  [navigate]
);

  /* ── FIX: upsertMerchant's action creator takes a single argument shaped
     as { merchantId?, data: Partial<Merchant> & { userId } } — every field
     nests under `data`. ─────────────────────────────────────────────── */
  const handleSubmit = async () => {
    const uid = user?.userId;
    if (!uid) {
      setError("Sign in to register a business.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const created = await dispatch(
        upsertMerchant({
          data: {
            userId: uid,
            businessName: form.businessName.trim(),
            businessType: form.businessType,
            country: form.country.code,
            email: form.email.trim() || user?.email || "",
            phone: form.phone.trim(),
            website: form.website.trim(),
            metadata: form.metadata,
          },
        })
      ).unwrap();

      setSuccess(true);
      await dispatch(fetchUserMerchants(uid)).unwrap();

      /* ═══ POST-ONBOARDING ROUTE — go straight into the new merchant's
         dashboard on the Subscriptions tab, not the org overview screen.
         The dashboard shell reads its initial tab off ?view=… on mount,
         so this drops the person right onto Subscriptions for the
         business they just created. ═══ */
   setSuccess(true);
await dispatch(fetchUserMerchants(uid)).unwrap();

const newId = (created as any)?.merchantId ?? (created as any)?.data?.merchantId;
redirectTimer.current = setTimeout(() => {
  if (newId) {
    navigate(`/subscription/${encodeURIComponent(newId)}`);
  } else {
    resetForm();
    setView("list");
  }
}, 1600);
} catch (err: any) {
  console.error(err);
  setError("Registration didn't go through. Check your details and try again.");
} finally {
  setLoading(false);
}
};

  const goStep1 = () => {
    if (!form.businessName.trim()) {
      setError("Add a business name to continue.");
      return;
    }
    setError(null);
    setStep(2);
  };

  const goStep2 = () => {
    if (!EMAIL_RE.test(form.email.trim())) {
      setError("Enter an email we can reach you at.");
      return;
    }
    setError(null);
    setStep(3);
  };

  const back = (to: number) => {
    setError(null);
    setStep(to);
  };

  const Bar = () => (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: isMobile ? "16px 18px" : "20px 26px",
        position: "relative",
        zIndex: 2,
      }}
    >
      <button onClick={() => navigate("/")} aria-label="Home" style={{ display: "flex", alignItems: "center" }}>
        <img src={logo} alt="Ebuos" style={{ height: 26, width: "auto" }} />
      </button>
    </header>
  );

  /* ═══════════════════════════════════════
     LIST VIEW
  ═══════════════════════════════════════ */
  if (view === "list") {
    return (
      <div className="eb">
        <style>{CSS}</style>
        <div className="eb-light" />
        <Bar />

        <div className="eb-shell" style={{ paddingBottom: 50 }}>
          <div
            className="eb-slab eb-rise"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div>
              <p className="eb-mono" style={{ color: "rgba(255,255,255,.65)" }}>Your businesses</p>
              <h1 style={{ fontSize: "clamp(24px,4vw,36px)", marginTop: 10 }}>Run it, your way. ✨</h1>
            </div>
            <motion.button
              whileHover={{ y: -3 }}
              whileTap={{ y: 4, scale: 0.98 }}
              className="eb-btn ghost"
              onClick={() => {
                resetForm();
                setView("form");
              }}
            >
              <Building2 size={17} strokeWidth={2.7} /> New organisation
            </motion.button>
          </div>

          {error && (
            <p style={{ marginTop: 14, color: RED, fontSize: 13 }}>{error}</p>
          )}

          <div style={{ marginTop: 26 }}>
            {storeLoading && !fetched && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ textAlign: "center", padding: 60, color: TEXT_DIM }}
              >
                <span className="eb-float" style={{ display: "block", fontSize: 30, marginBottom: 12 }}>🫧</span>
                Loading your organisations…
              </motion.div>
            )}

            {fetched && merchants.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="eb-plate"
                style={{ textAlign: "center", padding: isMobile ? "48px 20px" : "80px 40px" }}
              >
                <span className="eb-bead eb-float" style={{ width: 66, height: 66, margin: "0 auto 20px" }}>
                  <Building2 size={26} strokeWidth={2.4} />
                </span>
                <h2 style={{ fontSize: isMobile ? 24 : 30 }}>No organisations yet</h2>
                <p
                  style={{
                    marginTop: 12,
                    fontSize: 14,
                    color: TEXT_DIM,
                    maxWidth: 380,
                    marginLeft: "auto",
                    marginRight: "auto",
                    lineHeight: 1.6,
                  }}
                >
                  Register your first business and it lands here with its own dashboard.
                </p>
                <button
                  className="eb-btn primary"
                  style={{ marginTop: 22 }}
                  onClick={() => {
                    resetForm();
                    setView("form");
                  }}
                >
                  Create organisation <ArrowRight size={16} strokeWidth={2.8} />
                </button>
              </motion.div>
            )}

            {merchants.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : merchants.length === 1 ? "1fr" : "1fr 1fr",
                  gap: 16,
                }}
              >
                <AnimatePresence>
                  {merchants.map((m, i) => (
                    <MerchantCard
                      key={m.merchantId}
                      merchant={m}
                      index={i}
                      onSetUp={() => openMerchant(m)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════
     FORM VIEW
  ═══════════════════════════════════════ */
  return (
    <div className="eb" style={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}>
      <style>{CSS}</style>
      <div className="eb-light" />

      {/* left rail */}
      <div
        style={{
          width: isMobile ? "100%" : 340,
          background: EBUOS_GRADIENT_DIAG,
          padding: isMobile ? "14px 18px 20px" : "26px 30px",
          display: "flex",
          flexDirection: "column",
          position: isMobile ? "relative" : "sticky",
          top: 0,
          ...(isMobile ? {} : { height: "100vh" }),
        }}
      >
        <Bar />
        <button
          onClick={() => {
            resetForm();
            setView("list");
          }}
          className="eb-btn ghost sm"
          style={{ width: "fit-content", padding: "8px 16px", fontSize: 12, marginTop: 4 }}
        >
          ← Back to list
        </button>

        <div style={{ marginTop: isMobile ? 16 : 40 }}>
          <p className="eb-mono" style={{ color: "rgba(255,255,255,.6)" }}>New organisation</p>
        </div>

        {!isMobile && (
          <div className="eb-steps" style={{ flexDirection: "column", gap: 0, marginTop: 30 }}>
            {STEPS.map((s, i) => {
              const done = step > s.id;
              const active = step === s.id;
              return (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  style={{ display: "flex", alignItems: "flex-start", marginBottom: 26 }}
                >
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginRight: 16 }}>
                    <motion.span
                      animate={active ? { scale: [1, 1.14, 1] } : { scale: 1 }}
                      transition={{ duration: 0.4 }}
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: done || active ? "#fff" : "rgba(255,255,255,.16)",
                        color: done || active ? EBUOS_PINK : "rgba(255,255,255,.75)",
                        fontFamily: "'Fredoka',sans-serif",
                        fontWeight: 700,
                        fontSize: 13,
                        boxShadow: done || active ? "0 3px 0 rgba(0,0,0,.2)" : "none",
                      }}
                    >
                      {done ? "✓" : s.id}
                    </motion.span>
                    {i < STEPS.length - 1 && (
                      <span
                        style={{
                          width: 2,
                          height: 28,
                          marginTop: 6,
                          background: done ? "#fff" : "rgba(255,255,255,.22)",
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </div>
                  <div style={{ paddingTop: 4 }}>
                    <div
                      style={{
                        fontFamily: "'Fredoka',sans-serif",
                        fontWeight: 600,
                        fontSize: 16,
                        color: active || done ? "#fff" : "rgba(255,255,255,.55)",
                      }}
                    >
                      {s.label}
                    </div>
                    <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.55)", marginTop: 2 }}>{s.sub}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {isMobile && (
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            {STEPS.map((s) => (
              <motion.div
                key={s.id}
                animate={{
                  flex: step >= s.id ? 2 : 1,
                  background: step >= s.id ? "#fff" : "rgba(255,255,255,.24)",
                }}
                style={{ height: 3, borderRadius: 2 }}
              />
            ))}
          </div>
        )}
      </div>

      {/* right panel */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? "32px 20px 60px" : "56px 44px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ width: "100%", maxWidth: 500 }}>
          <AnimatePresence mode="wait">
            {step === 1 && !success && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
              >
                <p className="eb-mono" style={{ color: TEXT_FAINT, marginBottom: 10 }}>Step 01 / 03</p>
                <h1 style={{ fontSize: isMobile ? 30 : 38, marginBottom: 8 }}>Business details</h1>
                <p style={{ color: TEXT_DIM, fontSize: 14, lineHeight: 1.6, marginBottom: 30, maxWidth: 400 }}>
                  Tell us about the business you're registering.
                </p>

                <div style={{ marginBottom: 20 }}>
                  <label className="eb-label">Business name</label>
                  <div className="eb-field">
                    <input
                      value={form.businessName}
                      onChange={(e) => set("businessName", e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && goStep1()}
                      placeholder="e.g. Acme Corp"
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: 16,
                    marginBottom: 26,
                  }}
                >
                  <div>
                    <label className="eb-label">Business type</label>
                    <div className="eb-field" style={{ padding: 0 }}>
                      <select
                        value={form.businessType}
                        onChange={(e) => set("businessType", e.target.value)}
                        style={{
                          width: "100%",
                          background: "transparent",
                          border: "none",
                          outline: "none",
                          padding: "13px 16px",
                          fontSize: 14.5,
                          fontWeight: 600,
                          color: TEXT,
                          cursor: "pointer",
                        }}
                      >
                        {BUSINESS_TYPES.map((t) => (
                          <option key={t} value={t} style={{ background: SURFACE, color: TEXT }}>
                            {title(t)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <SphereSelect
                    label="Country"
                    items={countryItems}
                    value={form.country}
                    onChange={(it) => set("country", it)}
                    isMobile={isMobile}
                  />
                </div>

                <button className="eb-btn primary" style={{ width: "100%" }} onClick={goStep1}>
                  Continue <ArrowRight size={16} strokeWidth={2.8} />
                </button>
                {error && <p style={{ marginTop: 14, color: RED, fontSize: 13 }}>{error}</p>}
              </motion.div>
            )}

            {step === 2 && !success && (
              <motion.div
                key="s2"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
              >
                <p className="eb-mono" style={{ color: TEXT_FAINT, marginBottom: 10 }}>Step 02 / 03</p>
                <h1 style={{ fontSize: isMobile ? 30 : 38, marginBottom: 8 }}>Contact info</h1>
                <p style={{ color: TEXT_DIM, fontSize: 14, lineHeight: 1.6, marginBottom: 30, maxWidth: 400 }}>
                  Where should we reach this business?
                </p>

                <div style={{ marginBottom: 20 }}>
                  <label className="eb-label">Business email</label>
                  <div className="eb-field">
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && goStep2()}
                      placeholder="hello@acme.com"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label className="eb-label">Phone number</label>
                  <div className="eb-field">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder="+1 555 000 0000"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 26 }}>
                  <label className="eb-label">
                    Website{" "}
                    <span style={{ color: TEXT_FAINT, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
                  </label>
                  <div className="eb-field">
                    <input
                      value={form.website}
                      onChange={(e) => set("website", e.target.value)}
                      placeholder="https://acme.com"
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <button className="eb-btn outline" onClick={() => back(1)}>Back</button>
                  <button className="eb-btn primary" onClick={goStep2}>Continue</button>
                </div>
                {error && <p style={{ marginTop: 14, color: RED, fontSize: 13 }}>{error}</p>}
              </motion.div>
            )}

            {step === 3 && !success && (
              <motion.div
                key="s3"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4 }}
              >
                <p className="eb-mono" style={{ color: TEXT_FAINT, marginBottom: 10 }}>Step 03 / 03</p>
                <h1 style={{ fontSize: isMobile ? 30 : 38, marginBottom: 8 }}>Confirm & register</h1>
                <p style={{ color: TEXT_DIM, fontSize: 14, lineHeight: 1.6, marginBottom: 26, maxWidth: 400 }}>
                  Check the details, then register your organisation.
                </p>

                <div className="eb-plate" style={{ marginBottom: 24 }}>
                  {(
                    [
                      ["Business", form.businessName],
                      ["Type", title(form.businessType)],
                      ["Country", `${form.country.flag}  ${form.country.name}`],
                      ["Email", form.email],
                      ["Phone", form.phone || "—"],
                      ["Website", form.website || "—"],
                    ] as [string, string][]
                  ).map(([k, v], i, arr) => (
                    <div
                      key={k}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 14,
                        padding: "12px 2px",
                        boxShadow: i < arr.length - 1 ? `inset 0 -1px 0 ${BORDER}` : "none",
                      }}
                    >
                      <span className="eb-mono" style={{ color: TEXT_FAINT, flexShrink: 0 }}>{k}</span>
                      <span
                        style={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          textAlign: "right",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {v}
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <button className="eb-btn outline" onClick={() => back(2)} disabled={loading}>Back</button>
                  <button className="eb-btn primary" disabled={loading} onClick={handleSubmit}>
                    {loading ? "Registering…" : "Register"}
                  </button>
                </div>
                {error && <p style={{ marginTop: 14, color: RED, fontSize: 13 }}>{error}</p>}
              </motion.div>
            )}

            {success && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                style={{ textAlign: "center", paddingTop: isMobile ? 30 : 70, position: "relative" }}
              >
                <Confetti />
                <motion.span
                  className="eb-bead"
                  initial={{ scale: 0.4, rotate: -25 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 260, damping: 12 }}
                  style={{ width: 76, height: 76, margin: "0 auto 26px" }}
                >
                  <motion.svg
                    width="30"
                    height="30"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <motion.path
                      d="M5 13l4 4L19 7"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.15, duration: 0.55 }}
                    />
                  </motion.svg>
                </motion.span>
                <h1 style={{ fontSize: isMobile ? 28 : 36 }}>
                  {form.businessName || "Your organisation"} is live 🎉
                </h1>
                <p style={{ color: TEXT_DIM, fontSize: 14, marginTop: 10 }}>Opening your subscriptions…</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CONFETTI — tiny one-shot burst, purely decorative
═══════════════════════════════════════════════════════════ */
function Confetti() {
  const bits = useMemo(
    () =>
      Array.from({ length: 16 }, (_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 260,
        y: -60 - Math.random() * 120,
        rot: (Math.random() - 0.5) * 300,
        color: [EBUOS_BLUE, EBUOS_PINK, EBUOS_ORANGE, EBUOS_YELLOW, EBUOS_GREEN][i % 5],
        delay: Math.random() * 0.25,
      })),
    []
  );

  return (
    <div aria-hidden style={{ position: "absolute", left: "50%", top: 40, pointerEvents: "none" }}>
      {bits.map((b) => (
        <motion.span
          key={b.id}
          initial={{ opacity: 0, x: 0, y: 0, rotate: 0 }}
          animate={{ opacity: [0, 1, 1, 0], x: b.x, y: b.y, rotate: b.rot }}
          transition={{ duration: 1.4, delay: b.delay, ease: "easeOut" }}
          style={{
            position: "absolute",
            width: 7,
            height: 11,
            borderRadius: 2,
            background: b.color,
          }}
        />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MERCHANT CARD
═══════════════════════════════════════════════════════════ */
function MerchantCard({
  merchant,
  index,
  onSetUp,
}: {
  merchant: Merchant;
  index: number;
  onSetUp: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    []
  );

  const status =
    merchant.status === "active"
      ? { fg: SAGE, bg: SAGE_SOFT }
      : merchant.status === "pending"
      ? { fg: GOLD, bg: GOLD_SOFT }
      : { fg: RED, bg: RED_SOFT };

  const id = merchant.merchantId ?? "";
  const shortId = id ? `${id.slice(0, 14)}…` : "—";

  const copyId = async () => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -14 }}
      transition={{ delay: index * 0.06, duration: 0.4 }}
      className="eb-plate press"
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: "'Fredoka',sans-serif", fontWeight: 600, fontSize: 20 }}>
            {merchant.businessName || "Untitled"}
          </div>
          <div style={{ fontSize: 11.5, color: TEXT_DIM, marginTop: 5 }}>
            {title(merchant.businessType)} {DOT} {merchant.country || "—"} {DOT} {upper((merchant as any).currency)}
          </div>
        </div>
        <span
          className="eb-tag"
          style={{
            background: status.bg,
            color: status.fg,
            boxShadow: `inset 0 0 0 1.5px ${status.fg}55`,
            flexShrink: 0,
          }}
        >
          {merchant.status}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "18px 0" }}>
        <Cell label="Email" value={merchant.email || "—"} />
        <Cell label="Mode" value={merchant.mode === "live" ? "Live" : "Test"} />
        <div>
          <div className="eb-mono" style={{ color: TEXT_FAINT, fontSize: 8.5 }}>Merchant ID</div>
          <button
            type="button"
            onClick={copyId}
            title={id || undefined}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 4,
              fontSize: 12.5,
              color: copied ? SAGE : TEXT,
              fontWeight: 500,
              padding: 0,
            }}
          >
            {copied ? "Copied" : shortId}
            {copied ? <Check size={11} strokeWidth={3} /> : <Copy size={11} strokeWidth={2.4} style={{ color: TEXT_FAINT }} />}
          </button>
        </div>
        <Cell label="KYC" value={merchant.kycVerified ? "Verified" : "Pending"} />
      </div>

      <button className="eb-btn primary" style={{ width: "100%" }} onClick={onSetUp}>
        Set up <ArrowRight size={15} strokeWidth={2.8} />
      </button>
    </motion.div>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div className="eb-mono" style={{ color: TEXT_FAINT, fontSize: 8.5 }}>{label}</div>
      <div
        style={{
          fontSize: 12.5,
          color: TEXT,
          marginTop: 4,
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
    </div>
  );
}